import { observer } from "mobx-react-lite";
import type React from "react";
import { Avatar, type GetThemeValueForKey, View, XStack } from "tamagui";
import { useStores } from "../core/stores";
import { getImageInfo } from "../core/utils";
import { Body } from "./Body";
import { Link } from "./Link";
import { Subtext } from "./Subtext";
import { UserCircle } from "./UserCircle";

export enum AvatarSize {
  Tiny = "tiny",
  Smaller = "smaller",
  Small = "small",
  Medium = "medium",
  Large = "large",
}

export const AvatarSizeLookup: Record<AvatarSize, number> = {
  [AvatarSize.Tiny]: 24,
  [AvatarSize.Smaller]: 32,
  [AvatarSize.Small]: 40,
  [AvatarSize.Medium]: 50,
  [AvatarSize.Large]: 128,
};

type Props = { userUid?: string; size?: AvatarSize; withLink?: boolean };

export const UserAvatar = ({
  userUid,
  size = AvatarSize.Smaller,
  withLink,
}: Props) => {
  const { usersStore } = useStores();

  const user = usersStore.byId.get(userUid ?? "");

  if (!user) {
    return null;
  }

  const w = AvatarSizeLookup[size];

  let content: JSX.Element;
  if (!user.data.avatarUrl) {
    content = <UserCircle name={user.data.displayName} size={w} />;
  } else {
    const imageInfo = getImageInfo(user.data.avatarUrl);
    content = (
      <Avatar circular size={w} key={imageInfo.token}>
        <Avatar.Image
          source={{
            uri: imageInfo[size !== AvatarSize.Large ? 50 : 128],
            width: w,
            height: w,
          }}
        />
      </Avatar>
    );
  }

  if (!withLink) {
    return content;
  }

  return (
    <Link
      to={{
        screen: "Profile",
        params: { userUid: userUid ?? "", planUid: user.data.planUid },
      }}
    >
      {content}
    </Link>
  );
};

type UserProps = {
  displayName: string;
  avatarUrl: string | null;
  showBorder?: boolean;
  size?: AvatarSize | number;
};

export const UserPropsAvatar = ({
  displayName,
  avatarUrl,
  size = AvatarSize.Smaller,
  showBorder,
}: UserProps) => {
  const w = typeof size === "number" ? size : AvatarSizeLookup[size];
  const borderSize = w < 50 ? 2 : 3;

  let content: JSX.Element;
  if (!avatarUrl) {
    content = (
      <UserCircle name={displayName} size={w} showBorder={showBorder} />
    );
  } else {
    const imageInfo = getImageInfo(avatarUrl);
    content = (
      <Avatar
        circular
        size={w}
        key={imageInfo.token}
        backgroundColor={"white"}
        borderWidth={showBorder ? borderSize : undefined}
        borderColor={"$pressablePrimaryBackground"}
      >
        <Avatar.Image
          source={{
            uri: imageInfo[size !== AvatarSize.Large ? 50 : 128],
            width: w,
            height: w,
          }}
        />
      </Avatar>
    );
  }

  return content;
};

export const UserDisplay: React.FC<
  Props & {
    gap?: GetThemeValueForKey<"size">;
    subtext?: string | JSX.Element | undefined;
  }
> = observer(({ gap, subtext, ...props }) => {
  const { usersStore } = useStores();
  const user = usersStore.byId.get(props.userUid ?? "");

  return (
    <XStack alignItems="center" gap={gap ?? "$medium"}>
      <UserAvatar {...props} />
      <View>
        <Body>{user?.data.displayName ?? "Unknown"}</Body>
        {subtext && typeof subtext === "string" ? (
          <Subtext>{subtext}</Subtext>
        ) : (
          subtext
        )}
      </View>
    </XStack>
  );
});
